import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import { criticalMuiEmotionStyles } from "./criticalMuiEmotionStyles";

let cachedParentStyles: string | null = null;

const getCachedParentStyles = () => {
    if (cachedParentStyles !== null) return cachedParentStyles;

    cachedParentStyles = Array.from(document.styleSheets)
        .filter((sheet) => {
            try {
                return sheet.cssRules !== null;
            } catch (e) {
                return false;
            }
        })
        .map((sheet) => {
            try {
                return Array.from(sheet.cssRules)
                    .map((rule) => rule.cssText)
                    .join("\n");
            } catch (e) {
                return "";
            }
        })
        .join("\n");

    return cachedParentStyles;
};

type IframeStyleInjectorProps = {
    children: ReactNode;
    document: Document | undefined;
    onStylesLoaded?: () => void;
};

export const IframeStyleInjector = (props: IframeStyleInjectorProps) => {
    const { children, document: iFrameDoc, onStylesLoaded } = props;
    const [ready, setReady] = useState(false);
    const onStylesLoadedRef = useRef(onStylesLoaded);

    useEffect(() => {
        onStylesLoadedRef.current = onStylesLoaded;
    }, [onStylesLoaded]);

    useLayoutEffect(() => {
        if (!iFrameDoc || !iFrameDoc.head) return;

        const marker = iFrameDoc.getElementById("puck-styles-loaded");
        const existingCritical = iFrameDoc.getElementById("critical-styles");
        if (!existingCritical) {
            const criticalStyles = iFrameDoc.createElement("style");
            criticalStyles.id = "critical-styles";
            criticalStyles.textContent = criticalMuiEmotionStyles;
            iFrameDoc.head.appendChild(criticalStyles);
        }

        const existingParentStyles = iFrameDoc.getElementById("parent-styles");
        if (!existingParentStyles) {
            const allParentStyles = getCachedParentStyles();
            if (allParentStyles) {
                const parentStyles = iFrameDoc.createElement("style");
                parentStyles.id = "parent-styles";
                parentStyles.textContent = allParentStyles;
                iFrameDoc.head.appendChild(parentStyles);
            }
        }

        const googleFontsLink = document.querySelector('link[href*="fonts.googleapis.com"]') as HTMLLinkElement;
        if (googleFontsLink && googleFontsLink.href) {
            const existingFont = iFrameDoc.querySelector(`link[href="${googleFontsLink.href}"]`);
            if (!existingFont) {
                const fontLink = iFrameDoc.createElement("link");
                fontLink.rel = "stylesheet";
                fontLink.href = googleFontsLink.href;
                iFrameDoc.head.appendChild(fontLink);
            }
        }

        if (!marker) {
            const m = iFrameDoc.createElement("meta");
            m.id = "puck-styles-loaded";
            iFrameDoc.head.appendChild(m);
        }

        setReady(true);
        onStylesLoadedRef.current?.();
    }, [iFrameDoc]);

    return <div style={{ visibility: ready ? "visible" : "hidden" }}>{children}</div>;
};
